import { Icon } from '../ui/Icon';
import { compactNumber } from '../../lib/format';

/**
 * The stat tile — the right answer when the story is one number.
 *
 * Contract: label (sentence case, no trailing colon) · value · optional delta
 * signed against a named period · optional 12-point sparkline.
 *
 * Large standalone numbers use the font's default proportional figures.
 * `tabular-nums` gives every digit the width of a zero, which makes a value
 * like 121 look loose at display sizes; it is reserved for columns that have
 * to line up vertically.
 */
export function StatTile({
  label,
  value,
  unit,
  delta,
  deltaLabel,
  deltaGoodWhen = 'up',
  icon,
  tone,
  trend,
  compact = false,
  className = '',
}) {
  const deltaTone =
    delta === null || delta === undefined || delta === 0
      ? 'neutral'
      : (delta > 0) === (deltaGoodWhen === 'up')
        ? 'good'
        : 'critical';

  const deltaColor = {
    good: 'text-good-ink',
    critical: 'text-critical-ink',
    neutral: 'text-ink-3',
  }[deltaTone];

  return (
    <div
      className={`rounded-xl border border-line bg-surface p-3.5 shadow-card ${className}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[12.5px] text-ink-3">{label}</p>
        {icon && (
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-md ${
              tone === 'accent'
                ? 'bg-accent-soft text-accent-ink'
                : tone === 'critical'
                  ? 'bg-critical-soft text-critical-ink'
                  : tone === 'warning'
                    ? 'bg-warning-soft text-warning-ink'
                    : tone === 'good'
                      ? 'bg-good-soft text-good-ink'
                      : 'bg-brand-soft text-brand-ink'
            }`}
          >
            <Icon name={icon} size="xs" strokeWidth={2} />
          </span>
        )}
      </div>

      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span
          className={`font-semibold tracking-tight text-ink ${
            compact ? 'text-[22px]' : 'text-[28px]'
          }`}
        >
          {typeof value === 'number' ? compactNumber(value) : (value ?? '—')}
        </span>
        {unit && <span className="text-[13px] text-ink-3">{unit}</span>}
      </div>

      {(delta !== undefined && delta !== null) || trend ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          {delta !== undefined && delta !== null ? (
            <span className={`flex items-center gap-1 text-[12px] font-medium ${deltaColor}`}>
              <Icon
                name={delta === 0 ? 'dash' : 'arrow'}
                size="xs"
                strokeWidth={2}
                className={delta < 0 ? 'rotate-180' : ''}
              />
              {Math.abs(delta)}%
              {deltaLabel && <span className="font-normal text-ink-3">{deltaLabel}</span>}
            </span>
          ) : (
            <span />
          )}
          {trend && <Sparkline points={trend} tone={tone} />}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A sparkline is context, not a chart: no axes, no labels, no tooltip. The
 * current period sits in the accent so the eye lands on "now" first.
 */
export function Sparkline({ points, width = 64, height = 20, tone = 'brand' }) {
  const values = (points || []).filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);

  const path = values
    .map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / span) * (height - 4) - 2;
      return `${index ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join('');

  const lastX = width;
  const lastY = height - ((values[values.length - 1] - min) / span) * (height - 4) - 2;
  const stroke = tone === 'accent' ? 'var(--accent)' : 'var(--series-1)';

  return (
    <svg width={width} height={height} aria-hidden="true" className="overflow-visible">
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" opacity={0.55} />
      <circle cx={lastX} cy={lastY} r={2.5} fill="var(--accent)" stroke="var(--surface)" strokeWidth={1.5} />
    </svg>
  );
}
